import { useMemo, useState, type KeyboardEvent } from 'react';
import { FileCode, MessageSquare, AlertCircle } from 'lucide-react';
import { FileDiff, ReviewComment } from '../types';
import { tokenizeFile, commentLinesFor } from '../lib/diff';

interface DiffViewerProps {
  files: FileDiff[];
  comments?: ReviewComment[];
  /** Called when the user clicks a comment pin (or a commented line). */
  onCommentClick?: (commentId: string) => void;
}

/**
 * Unified-diff viewer with a file switcher, line-numbered gutter, +/- coloring,
 * and comment pins on lines the review flagged.
 *
 * Replaces the old Code tab that dumped `files[0].content` into a <pre>.
 * When a file's `diff` is empty (currently always — the worker hasn't captured
 * a unified diff yet), it renders `content` as plain numbered context and shows
 * a notice; once iteration 2 populates `diff`, +/- markers light up.
 */
export function DiffViewer({ files, comments = [], onCommentClick }: DiffViewerProps) {
  const [active, setActive] = useState(0);

  const file = files[active];
  const lines = useMemo(() => (file ? tokenizeFile(file) : []), [file]);
  const commented = useMemo(
    () => (file ? commentLinesFor(lines, comments, file.filepath) : new Set<number>()),
    [lines, comments, file],
  );

  // First comment id per line (for click-to-focus).
  const commentIdByLine = useMemo(() => {
    const m = new Map<number, string>();
    for (const c of comments) {
      if (c.filepath && file && c.filepath !== file.filepath) continue;
      if (c.line <= 0 || m.has(c.line)) continue;
      m.set(c.line, c.id);
    }
    return m;
  }, [comments, file]);

  function onKeyDown(e: KeyboardEvent<HTMLDivElement>) {
    if (files.length < 2) return;
    if (e.key === 'ArrowRight') {
      e.preventDefault();
      setActive((a) => (a + 1) % files.length);
    } else if (e.key === 'ArrowLeft') {
      e.preventDefault();
      setActive((a) => (a - 1 + files.length) % files.length);
    }
  }

  if (!file) {
    return (
      <div className="bg-white border border-[#EDEDEB] rounded-xl p-10 flex flex-col items-center text-[#91918E]" id="diff-empty">
        <FileCode className="w-10 h-10 mb-2 text-[#91918E]/40" />
        <p className="text-xs font-mono">No files in this pull request.</p>
      </div>
    );
  }

  const hasDiff = Boolean(file.diff && file.diff.trim());

  return (
    <div className="bg-white border border-[#EDEDEB] rounded-xl overflow-hidden" id="diff-viewer">
      {/* File switcher */}
      {files.length > 1 && (
        <div
          onKeyDown={onKeyDown}
          tabIndex={0}
          role="tablist"
          aria-label="Changed files"
          className="flex gap-1 overflow-x-auto bg-[#F7F7F5] border-b border-[#EDEDEB] px-2 py-2 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#191919]/30"
          id="diff-file-switcher"
        >
          {files.map((f, i) => (
            <button
              key={f.filepath + i}
              role="tab"
              aria-selected={i === active}
              onClick={() => setActive(i)}
              className={`flex items-center gap-2 px-3 py-1.5 rounded-md text-xs font-mono whitespace-nowrap transition ${
                i === active
                  ? 'bg-white text-[#191919] border border-[#EDEDEB] shadow-sm'
                  : 'text-[#91918E] hover:text-[#191919] hover:bg-white/60'
              }`}
            >
              <FileCode className="w-3.5 h-3.5 shrink-0" />
              <span>{f.filepath}</span>
              <span className="text-[10px] font-semibold">
                <span className="text-[#2E7D32]">+{f.additions}</span>
                <span className="text-[#91918E]"> </span>
                <span className="text-[#C62828]">-{f.deletions}</span>
              </span>
            </button>
          ))}
        </div>
      )}

      {/* Active file header */}
      <div
        className="bg-[#F7F7F5] p-4 border-b border-[#EDEDEB] flex items-center justify-between text-xs font-mono"
        id="diff-header"
      >
        <div className="flex items-center gap-2 text-[#191919] font-medium min-w-0">
          <FileCode className="w-4 h-4 text-[#191919] shrink-0" />
          <span className="truncate">{file.filepath}</span>
        </div>
        <div className="flex items-center gap-3 font-semibold text-[11px] shrink-0">
          <span className="text-[#2E7D32]">+{file.additions}</span>
          <span className="text-[#C62828]">-{file.deletions}</span>
        </div>
      </div>

      {/* No-diff notice (honest, not a fake "diff") */}
      {!hasDiff && (
        <div className="flex items-center gap-2 px-4 py-2 bg-[#FFF8E1] border-b border-[#FFE082] text-[#91700c] text-[11px] font-mono" id="diff-no-diff-notice">
          <AlertCircle className="w-3.5 h-3.5 shrink-0" />
          <span>Showing file contents — no unified diff captured yet (+/− markers appear once the review records one).</span>
        </div>
      )}

      {/* Lines */}
      <div
        className="overflow-x-auto font-mono text-xs bg-white leading-relaxed"
        id="diff-content-view"
      >
        <table className="w-full border-collapse">
          <tbody>
            {lines.map((ln, i) => {
              const pinned = commented.has(ln.num);
              const cid = pinned ? commentIdByLine.get(ln.num) : undefined;
              const rowBg =
                ln.type === 'add' ? 'bg-[#F1F8E9]' : ln.type === 'del' ? 'bg-[#FFF5F5]' : 'bg-white';
              const marker = ln.type === 'add' ? '+' : ln.type === 'del' ? '−' : ' ';
              const txtCls =
                ln.type === 'add'
                  ? 'text-[#2E7D32]'
                  : ln.type === 'del'
                    ? 'text-[#C62828]'
                    : 'text-[#191919]';
              return (
                <tr key={i} className={`${rowBg} ${pinned ? 'ring-1 ring-inset ring-amber-300' : ''}`}>
                  <td className="select-none text-right pr-2 pl-3 w-12 align-top text-[#91918E] border-r border-[#F2F2F0]">
                    {ln.num > 0 ? ln.num : ''}
                  </td>
                  <td className={`select-none text-center w-6 align-top ${txtCls}`}>{marker}</td>
                  <td className="pr-4 align-top">
                    {pinned ? (
                      <button
                        type="button"
                        onClick={() => cid && onCommentClick?.(cid)}
                        title={`Comment on line ${ln.num} — click to jump`}
                        aria-label={`Line ${ln.num} has a review comment. Jump to comment.`}
                        className={`group flex items-start gap-1.5 w-full text-left ${txtCls}`}
                      >
                        <MessageSquare className="w-3.5 h-3.5 mt-0.5 text-amber-500 shrink-0" />
                        <span className="whitespace-pre-wrap break-all flex-1">{ln.text || ' '}</span>
                      </button>
                    ) : (
                      <span className={`whitespace-pre-wrap break-all block ${txtCls}`}>{ln.text || ' '}</span>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
